import React from "react"

import {
  Favorites,
  getFormattedDate,
  Avatar,
  FilterItem,
  FilterDatePicker,
  CustomUsersTarget,
  CustomGetUsersOptionLabel,
  Tooltip,
  Person,
} from "@dit/core-frontend"
import { EntityLink } from "@components/entity"
import { Box, Text } from "@mos-cat/ds"
import { PageStatus } from "@components/pages/PageStatus"

import { API_ENDPOINTS, ROUTES } from "@constants"

export const HISTORY_WORK_TYPE = {
  VIEW: "view",
  EDIT: "edit",
}

export const PAGE_TITLE = {
  VIEW: "Недавно просмотренные",
  EDIT: "Недавно работал",
}

export const EMPTY_TEXT = {
  VIEW: "Нет недавно просмотренных",
  EDIT: "Нет недавно измененных",
}

export const LIMIT = 16

export const defaultFilterParams = {
  dateFrom: "",
  dateTo: "",
  users: [],
  zones: [],
  statuses: [],
  createdAtFrom: "",
  createdAtTo: "",
  modifiedAtFrom: "",
  modifiedAtTo: "",
  archive: "",
}

export const filtersList = [
  {
    title: "Разделы:",
    endPoint: API_ENDPOINTS.zoneList,
    key: "zones",
    component: FilterItem,
  },
  {
    title: "Автор",
    endPoint: API_ENDPOINTS.users,
    key: "users",
    customValue: "firstName",
    customGetOptionLabel: (option) => CustomGetUsersOptionLabel(option),
    customTarget: ({ value, title, defaultText, isOpen, setIsOpen, handleCleanFilter }) => (
      <CustomUsersTarget
        value={value}
        title={title}
        defaultText={defaultText}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleCleanFilter={handleCleanFilter}
      />
    ),
    component: FilterItem,
  },
  {
    title: "Дата создания",
    key: "date_create",
    defaultText: "Не выбрано",
    component: FilterDatePicker,
    filterKey: "createdAt",
  },
  {
    title: "Дата обновления",
    key: "date_update",
    defaultText: "Не выбрано",
    component: FilterDatePicker,
    filterKey: "modifiedAt",
  },
  {
    title: "Статус:",
    endPoint: API_ENDPOINTS.pageStatusList,
    key: "statuses",
    component: FilterItem,
  },
  {
    title: "Период работы",
    key: "date_work",
    defaultText: "Не выбрано",
    component: FilterDatePicker,
    filterKey: "date",
  },
  {
    title: "Только архивные:",
    endPoint: "",
    key: "archive",
    defaultText: "Нет",
    optionsList: [{ id: "true", title: "Да", placeholder: "Да" }],
    isMultiValue: false,
    component: FilterItem,
  },
]

export const onSearchItems = (value, searchParams, navigate) => {
  const params = new URLSearchParams(searchParams)
  if (value) {
    params.set("search", (value || "").trim())
  } else {
    params.delete("search")
  }

  navigate(`${location.pathname}?${params}`)
}

const getCellPropsByStatus = (page) => {
  if (page.archive) {
    return {
      opacity: "0.4",
    }
  }
  return {}
}

export const createTableConfig = (updateResponseData) => {
  const isRecentWorkRequest = true

  const cells = [
    {
      label: "",
      key: "favorites",
      width: "30px",
      getContent: (item) => (
        <Box display="flex" alignItems="center" justifyContent="flex-start">
          <Favorites
            itemId={item.page.id}
            favoritesId={item.page.favorites?.id}
            type="page"
            requestUrl={API_ENDPOINTS.favorites}
            iconSize={21}
            onSuccess={(favoritesId) => {
              updateResponseData(
                [item.page.id],
                "update",
                {
                  favorites: favoritesId ? { id: favoritesId } : null,
                },
                isRecentWorkRequest,
              )
            }}
          />
        </Box>
      ),
    },
    {
      label: "Страница",
      key: "slug",
      getContent: (item) => (
        <EntityLink
          entity={item.page}
          isEntityPage
          link={`${ROUTES.ZONE}/${item.page.zone.slug}/pages`}
          visibleField="title"
          lines={1}
          style={{ wordBreak: "break-all" }}
        />
      ),
    },
    {
      label: "Раздел",
      key: "zone",
      getContent: (item) => {
        return (
          <Box
            display="flex"
            alignItems="center"
            gridGap="10px"
            overflow="hidden"
            {...getCellPropsByStatus(item.page)}
          >
            <Avatar url={item.page.zone.icon?.url || " "} size={28} borderRadius="0" />
            <Tooltip content={`${item.page.zone.title}`} style={{ overflow: "hidden" }}>
              <EntityLink
                entity={item.page.zone}
                customVisibleField={item.page.zone.title}
                link={ROUTES.ZONE}
                lines={2}
              />
            </Tooltip>
          </Box>
        )
      },
    },
    {
      label: "Автор",
      key: "user",
      getContent: (item) => (
        <Person
          person={item.page.user}
          isProfileLink
          {...getCellPropsByStatus(item.page)}
          avatarMarginTop="0"
          maxLines={1}
          alignItems="center"
        />
      ),
    },
    {
      label: "Создана",
      key: "createdAt",
      width: "120px",
      getContent: (item) => (
        <Text as={Box} fontSize="fs-14" {...getCellPropsByStatus(item.page)}>
          {getFormattedDate(item.page.createdAt)}
        </Text>
      ),
    },
    {
      label: "Обновлена",
      key: "modifiedAt",
      width: "120px",
      getContent: (item) => (
        <Box display="flex" alignItems="center">
          <Text as={Box} fontSize="fs-14" {...getCellPropsByStatus(item.page)}>
            {getFormattedDate(item.page.modifiedAt)}
          </Text>
        </Box>
      ),
    },
    {
      label: "Статус",
      key: "status",
      width: "150px",
      justifyContent: "left",
      getContent: (item) => (
        <Box display="flex" alignItems="center" {...getCellPropsByStatus(item.page)}>
          <PageStatus status={item.page.status} />
        </Box>
      ),
    },
  ]

  return { cells }
}
